import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { AccountCredit } from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  IPractice,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  getError,
  snapshotCombineLatest,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientDepositDestinationEntity,
  IDepositBuildData,
  IDepositJobData,
  IDepositMigrationData,
} from '../../../destination/entities/patient-deposits';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { type TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientDeposit,
  PatientDepositSourceEntity,
} from '../../source/entities/patient-deposit';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { PatientDestinationEntity } from './patients';

export class PatientDepositDestinationEntity extends BasePatientDepositDestinationEntity<
  D4WPatient,
  IDepositJobData<D4WPatient>
> {
  override patientSourceEntity = new PatientSourceEntity();
  override filters = [
    new PracticeIdFilter<IDepositJobData<D4WPatient>>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<IDepositJobData<D4WPatient>>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    deposits: new PatientDepositSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    practices: new D4WPracticeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IDepositJobData<D4WPatient>[]> {
    const practices$ = this.customMappings.practices.getRecords(translationMap);

    return snapshotCombineLatest([practices$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [practices]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          practices,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IDepositJobData<D4WPatient>
  ): Promise<
    | IDepositMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const patientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        patientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(data.sourcePatient, 'No patient found');
      }

      const sourceDeposits = await this.sourceEntities.deposits.filterRecords(
        migration,
        'patientId',
        patientId
      );

      const deposits = sourceDeposits.map((deposit) => {
        const depositData = this._buildDepositData(deposit, data.practices);

        if (!depositData) {
          throw new Error(`Deposit ${deposit.record.uid} has no amount`);
        }

        return {
          ...depositData,
          transactions: [],
          createdAt: deposit.record.translations.createdAt,
        };
      });

      return {
        patientRef,
        deposits: sortBy(deposits, 'uid'),
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  private _buildDepositData(
    deposit: IGetRecordResponse<D4WPatientDeposit>,
    practices: WithPrimaryKey<ITranslationMap<IPractice>>[]
  ): IDepositBuildData | undefined {
    const used = roundTo2Decimals(parseFloat(deposit.data.deposit_used_total));
    const amount = roundTo2Decimals(
      parseFloat(deposit.data.deposit_amount_total)
    );

    if (!amount) {
      return;
    }

    const uid = this.sourceEntities.deposits
      .getSourceRecordId(deposit.data)
      .toString();

    const practiceSourceId = deposit.data.practice_id.toString();
    const practiceMap = practices.find(
      (practice) => practice.sourceIdentifier === practiceSourceId
    );

    if (!practiceMap?.destinationIdentifier) {
      throw new Error(`No practice with id ${practiceSourceId}`);
    }

    return {
      uid,
      accountCredit: {
        ...AccountCredit.init({
          type: AccountCreditType.Deposit,
          description: `Deposit ${deposit.data.original_total_payment_id}`,
          amount,
          used: roundTo2Decimals(used),
          practiceRef: practiceMap.destinationIdentifier,
        }),
        createdAt: deposit.record.translations.createdAt,
        issued: deposit.record.translations.createdAt,
      },
      transactions: [],
    };
  }
}
